import React, { useEffect, useState, useRef, useMemo } from 'react';
import { BubbleInput, BubbleButton, BubbleAlert } from '@go-client/components/BubbleUI';
import { safeIdCompare, hasOwnProperty } from '@go-shared/lib/utils';
import { mapBy } from '@go-shared/lib/functional';
import { replaceTranslationByRefer, getTranslations } from '@go-client/lib/api/translation';
import { RouteComponentProps, Prompt } from 'react-router-dom';
import Fuse from 'fuse.js';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import TranslationBlock from '@go-client/components/TranslationBlock';
import Translations from './Translations';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  generateDefaultNewTranslation,
  makeVenueTypeTranslationTransform,
  TranslationFilters,
} from '@go-shared/lib/translation';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandVenueType } from '@go-shared/types/brand';
import {
  VenueTypeTransforms,
  VenueTypeTranslatable,
  VenueTypeTranslation,
} from '@go-shared/types/translation';
import TranslationsSubNavigation from '../../components/Navigations/TranslationsSubNavigation';
import { canModifyBrandTranslation } from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';

interface VenueTypeTranslationDataBundle {
  venueTypes: BrandVenueType[];
  venueTypeTranslations: VenueTypeTranslation[];
  venueTypesMap: { [key: string]: BrandVenueType };
  venueTypeTranslationsMap: { [key: string]: VenueTypeTranslation };
}

interface VenueTypeTranslationForm {
  dirty: boolean;
  transforms: VenueTypeTransforms;
}

interface ChangeVenueTypeFormAction {
  active?: boolean;
  valueChange?: { key: keyof VenueTypeTranslatable; value: string };
}

type VenueTypeTranslationsPageProps = RouteComponentProps;

export default function VenueTypeTranslations(props: VenueTypeTranslationsPageProps) {
  const [loading, setLoading] = useState(true);
  const [dataBundle, setDataBundle] = useState<VenueTypeTranslationDataBundle | undefined>(
    undefined
  );
  const [activeVenueType, setActiveVenueType] = useState<BrandVenueType | undefined>(undefined);
  const [venueTypeTranslationForm, setVenueTypeTranslationForm] = useState<
    VenueTypeTranslationForm | undefined
  >(undefined);
  const [search, setSearch] = useState('');

  const contentRef = useRef<HTMLDivElement | null>(null);

  const { activeProperty } = useActiveProperty();

  const serverInfo = useServerInfo();
  const { brand, languages } = serverInfo;
  const { user } = useUserInfo();

  useEffect(() => {
    setLoading(true);
    fetchDataBundle().catch(console.error);
  }, [activeProperty]);

  // this will refresh the currently active venue type if data bundle refreshes
  useEffect(() => {
    if (!dataBundle) return;

    setActiveVenueType(state => {
      const brandVenueType = dataBundle.venueTypes[0];
      if (!state) return brandVenueType;
      const refreshedVenueType = dataBundle.venueTypesMap[state._id];
      return refreshedVenueType || brandVenueType;
    });
  }, [dataBundle]);

  // this will reset the form each time a new venue type is clicked
  useEffect(() => {
    resetVenueTypesForm();
  }, [activeVenueType]);

  // this will scroll the page to top when search is modified
  useEffect(() => {
    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [search, contentRef]);

  const fuse = useMemo(() => {
    if (!dataBundle) return undefined;

    return new Fuse(dataBundle.venueTypes, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name'],
    });
  }, [dataBundle]);

  const fuseSearchResults = useMemo(() => {
    if (!search || !fuse) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const filteredVenueTypes = !dataBundle ? [] : fuseSearchResults || dataBundle.venueTypes;

  async function fetchDataBundle() {
    try {
      const venueTypes = brand.venueTypes;
      const venueTypeTranslations = await getTranslations('venuetype');

      const venueTypesMap = mapBy(venueTypes, '_id');
      const venueTypeTranslationsMap = mapBy(venueTypeTranslations, 'refers');

      setDataBundle({
        venueTypes,
        venueTypeTranslations,
        venueTypesMap,
        venueTypeTranslationsMap,
      });
    } finally {
      setLoading(false);
    }
  }

  function resetVenueTypesForm() {
    const transforms = activeVenueType
      ? makeVenueTypeTranslationTransform(
          languages.map(l => l.key),
          dataBundle?.venueTypeTranslationsMap[activeVenueType._id]
        )
      : undefined;

    if (!transforms) {
      setVenueTypeTranslationForm(undefined);
      return;
    }

    const formData: VenueTypeTranslationForm = {
      dirty: false,
      transforms,
    };

    setVenueTypeTranslationForm(formData);
  }

  function changeVenueTypeForm(lang: string, change: ChangeVenueTypeFormAction) {
    if (!venueTypeTranslationForm) return;

    const updatedForm = { ...venueTypeTranslationForm, dirty: true };

    if (!hasOwnProperty(updatedForm.transforms, lang)) {
      return;
    }

    updatedForm.transforms[lang].modified = true;

    if (change.active !== undefined) updatedForm.transforms[lang].active = change.active;
    if (change.valueChange !== undefined) {
      updatedForm.transforms[lang].delta[change.valueChange.key] = change.valueChange.value;
    }

    setVenueTypeTranslationForm(updatedForm);
  }

  async function handleSubmitVenueTypeForm() {
    if (
      !venueTypeTranslationForm ||
      !activeVenueType ||
      !dataBundle ||
      !canModifyBrandTranslation(user, serverInfo)
    )
      return;

    const translation = generateDefaultNewTranslation(activeVenueType._id, 'venuetype');

    translation.transforms = venueTypeTranslationForm.transforms;

    await replaceTranslationByRefer(activeVenueType._id, translation);
    await fetchDataBundle();
    setVenueTypeTranslationForm({ ...venueTypeTranslationForm, dirty: false });
  }

  function headerFn() {
    return <TranslationsSubNavigation active="venueTypes" property={activeProperty} />;
  }

  function leftFn() {
    return (
      <>
        <div
          className="sticky flex bg-white border-b"
          style={{ top: 'calc(130px + 40px + 0.75rem)', height: '50px', zIndex: 3 }}
        >
          <BubbleInput
            type="text"
            block
            appearance="bare"
            placeholder="Search..."
            className="flex-grow"
            inputStyle={{ height: '49px' }}
            inputClassName="px-5"
            value={search}
            onValue={setSearch}
          />
        </div>
        <div style={{ paddingTop: '50px' }}>
          {filteredVenueTypes.map(venueType => (
            <div
              key={venueType._id}
              className={`list-item px-5 py-3 cursor-pointer ${
                activeVenueType && safeIdCompare(activeVenueType._id, venueType._id) ? 'active' : ''
              }`}
              onClick={() => setActiveVenueType(venueType)}
            >
              <div className="flex items-center">
                <div className="overflow-ellipsis">
                  <div className="list-item-name font-semibold">{venueType.name}</div>
                </div>
                <FontAwesomeIcon
                  icon="chevron-right"
                  className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                />
              </div>
            </div>
          ))}
          {filteredVenueTypes.length < 1 && (
            <div className="grid items-center justify-center italic text-smoke bg-eggshell px-3 py-2">
              No venue types found
            </div>
          )}
        </div>
      </>
    );
  }

  function rightFn({ selectedLanguages }: TranslationFilters) {
    return (
      <>
        {dataBundle && activeVenueType && venueTypeTranslationForm && (
          <>
            <div style={{ marginBottom: '-1px' }}>
              {!canModifyBrandTranslation(user, serverInfo) && (
                <BubbleAlert className="mt-3 mx-3" variant="ocean">
                  This venue type cannot be modified unless you're in a brand instance and a brand
                  admin.
                </BubbleAlert>
              )}
              <TranslationBlock
                label="Name"
                value={activeVenueType.name}
                languages={selectedLanguages}
                charCountFn={({ language }) => (
                  <span className="text-smoke-light">
                    ({venueTypeTranslationForm.transforms[language.key].delta.name.length})
                  </span>
                )}
              >
                {({ language }) => (
                  <div className="flex items-start">
                    <BubbleButton
                      inverse
                      variant={
                        !venueTypeTranslationForm.transforms[language.key].active ? 'smoke' : 'navy'
                      }
                      className="px-1-i pt-2 mr-2 leading-none"
                      onClick={() =>
                        changeVenueTypeForm(language.key, {
                          active: !venueTypeTranslationForm.transforms[language.key].active,
                        })
                      }
                      icon={
                        venueTypeTranslationForm.transforms[language.key].active
                          ? 'eye'
                          : 'eye-slash'
                      }
                    />
                    <BubbleInput
                      type="text"
                      block
                      className="flex-grow"
                      inputClassName={
                        !venueTypeTranslationForm.transforms[language.key].active
                          ? 'italic text-smoke'
                          : ''
                      }
                      value={venueTypeTranslationForm.transforms[language.key].delta.name}
                      onValue={val =>
                        changeVenueTypeForm(language.key, {
                          valueChange: { key: 'name', value: val },
                        })
                      }
                    />
                  </div>
                )}
              </TranslationBlock>
            </div>

            <div className="sticky bottom-0 p-3 bg-eggshell border-t">
              <BubbleButton
                onClick={() => handleSubmitVenueTypeForm()}
                variant="navy"
                disabled={
                  !venueTypeTranslationForm.dirty || !canModifyBrandTranslation(user, serverInfo)
                }
                icon="floppy-disk"
              >
                Save Changes
              </BubbleButton>
              <BubbleButton
                variant="slate"
                disabled={!venueTypeTranslationForm.dirty}
                inverse
                onClick={() => resetVenueTypesForm()}
                icon="rotate-left"
              >
                Reset
              </BubbleButton>
            </div>
          </>
        )}
      </>
    );
  }

  // this adds a check to prevent closing tab if unsaved changes
  useEffect(() => {
    if (venueTypeTranslationForm && venueTypeTranslationForm.dirty) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  });

  return (
    <>
      <Prompt
        when={venueTypeTranslationForm && venueTypeTranslationForm.dirty}
        message="You have unsaved changes! Are you sure you want to leave?"
      />
      <Translations
        {...props}
        category="venueTypes"
        loading={loading}
        contentRef={contentRef}
        property={activeProperty}
        languages={languages}
        leftFn={leftFn}
        rightFn={rightFn}
        headerFn={headerFn}
      />
    </>
  );
}
